/**
 * ScenarioFormProvider
 *
 * Wraps scenario editing with React Hook Form context.
 * This allows child components to gradually migrate to useFormContext
 * while the parent continues to use the legacy useScenarioForm.
 *
 * Usage in ScenarioEditor:
 * ```jsx
 * <ScenarioFormProvider initialData={loadedScenarioData}>
 *   {(rhfMethods) => (
 *     <SiteConfiguration />
 *     <SolarConfiguration />
 *   )}
 * </ScenarioFormProvider>
 * ```
 *
 * Usage in child components:
 * ```jsx
 * // Option 1: Use props (current behavior)
 * function SiteConfiguration({ siteName, setSiteName }) { ... }
 *
 * // Option 2: Use form context (new behavior)
 * function SiteConfiguration() {
 *   const { watch, setValue } = useFormContext();
 *   const siteName = watch('site.siteName');
 *   // ...
 * }
 * ```
 */

import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ScenarioFormSchema } from '@vortx/shared-ui';

/**
 * Default form values matching legacy useScenarioForm
 */
const defaultValues = {
  site: {
    scenarioName: '',
    siteName: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    siteDescription: '',
    facilityType: 'commercial',
    utilityName: '',
    serviceClass: 'commercial',
    offGrid: false,
    latitude: null,
    longitude: null,
  },
  solar: {
    enabled: false,
    sizingMode: 'prescribed',
    maxKw: '',
    minKw: '0',
    costPerKw: '1600',
    exportMode: 'none',
    existingKw: '',
  },
  battery: {
    enabled: false,
    sizingMode: 'prescribed',
    maxKw: '',
    maxKwh: '',
    minKw: '0',
    minKwh: '0',
    costPerKwh: '500',
    reservePercent: '20',
    chargeSource: 'pv_and_grid',
    canGridCharge: true,
    batteries: [{ id: 1, name: 'Battery 1', kw: '', kwh: '' }],
  },
  generator: {
    enabled: false,
    sizingMode: 'prescribed',
    maxKw: '',
    minKw: '0',
    fuelType: 'diesel',
    fuelCostPerGallon: '3',
    fuelCostPerMmbtu: '6',
    exportMode: 'none',
    runHoursLimit: '',
    uptimePercent: '96.5',
    thermalEfficiency: '',
    generators: [],
  },
  tariff: {
    source: 'blended',
    tariffId: null,
    blendedEnergyRate: '0.12',
    blendedDemandRate: '15.0',
    monthlyFixedCharge: '25.0',
    touData: null,
    selectedTariffData: null,
    exportConfig: {
      enabled: false,
      mode: 'none',
      rate: '',
      tariffId: null,
      tariffData: null,
    },
    caDerConfig: null,
    gasTariffId: null,
    gasTariffData: null,
  },
  ui: {
    inputMode: 'ai_assisted',
    aiExtractionComplete: false,
    aiExtractedData: null,
    calculationMethod: 'reopt',
    activeTab: 0,
  },
  commercialAssumptions: null,
  dealAnalysisConfig: null,
};

/**
 * ScenarioFormProvider Component
 *
 * @param {Object} props
 * @param {Object} props.initialData - Initial form data (from loaded scenario)
 * @param {Object} props.legacyFormState - Current state from useScenarioForm (for sync)
 * @param {Function} props.onFormChange - Callback when form values change (receives mapped RHF values)
 * @param {Function} props.syncToLegacy - Callback to sync specific field changes back to legacy setters
 * @param {React.ReactNode|Function} props.children - Child components or render function
 */
export function ScenarioFormProvider({
  initialData,
  legacyFormState,
  onFormChange,
  syncToLegacy,
  children,
}) {
  // Initialize React Hook Form
  const methods = useForm({
    resolver: zodResolver(ScenarioFormSchema),
    defaultValues: initialData || defaultValues,
    mode: 'onChange',
  });

  const { watch, reset, setValue, getValues } = methods;

  // Track if we're currently syncing from legacy to prevent loops
  const isSyncingFromLegacy = useRef(false);
  // Track previous values to detect actual changes
  const prevRhfValues = useRef(null);

  // Sync legacy form state changes to RHF (legacy → RHF)
  // This allows the old form hooks to continue working while
  // child components can optionally use RHF context
  useEffect(() => {
    if (!legacyFormState) return;

    // Map legacy state to RHF structure
    const mappedValues = mapLegacyToRHF(legacyFormState);

    // Only update if different to avoid infinite loops
    const currentValues = getValues();
    if (JSON.stringify(mappedValues) !== JSON.stringify(currentValues)) {
      isSyncingFromLegacy.current = true;
      reset(mappedValues, { keepDirty: true, keepTouched: true });
      prevRhfValues.current = mappedValues;
      // Reset the flag after a tick to allow normal RHF updates
      setTimeout(() => {
        isSyncingFromLegacy.current = false;
      }, 0);
    }
  }, [legacyFormState, reset, getValues]);

  // Watch for RHF changes and sync back to legacy (RHF → legacy)
  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      // Skip if this change came from legacy sync
      if (isSyncingFromLegacy.current) return;

      // Call the general change handler if provided
      if (onFormChange) {
        onFormChange(values);
      }

      // Sync specific field changes back to legacy setters.
      // Note: RHF's setValue() does not emit type='change' (only registered
      // inputs do), so we sync on any named field update.
      if (syncToLegacy && name) {
        syncToLegacy(name, values);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, onFormChange, syncToLegacy]);

  // Memoize the methods to pass to children
  const contextValue = useMemo(() => ({
    ...methods,
    // Helper to check if using RHF or legacy
    isRHFEnabled: true,
  }), [methods]);

  // Support render function pattern for more control
  if (typeof children === 'function') {
    return (
      <FormProvider {...contextValue}>
        {children(contextValue)}
      </FormProvider>
    );
  }

  return (
    <FormProvider {...contextValue}>
      {children}
    </FormProvider>
  );
}

/**
 * Map legacy useScenarioForm state to RHF structure
 */
function mapLegacyToRHF(legacy) {
  if (!legacy) return defaultValues;

  return {
    site: {
      scenarioName: legacy.scenarioName || '',
      siteName: legacy.siteName || '',
      street: legacy.street || '',
      city: legacy.city || '',
      state: legacy.state || '',
      zipCode: legacy.zipCode || '',
      siteDescription: legacy.siteDescription || '',
      facilityType: legacy.facilityType || 'commercial',
      utilityName: legacy.utilityName || '',
      serviceClass: legacy.serviceClass || 'commercial',
      offGrid: legacy.offGrid || false,
      latitude: legacy.latitude || null,
      longitude: legacy.longitude || null,
    },
    solar: {
      enabled: legacy.usePv || false,
      sizingMode: legacy.sizingMode || 'prescribed',
      maxKw: legacy.pvMaxKw || '',
      minKw: legacy.pvMinKw || '0',
      costPerKw: legacy.pvCost || '1600',
      exportMode: legacy.pvExportMode || 'none',
      existingKw: legacy.existingKw || '',
    },
    battery: {
      enabled: legacy.useStorage || false,
      sizingMode: legacy.batterySizingMode || 'prescribed',
      maxKw: legacy.storageMaxKw || '',
      maxKwh: legacy.storageMaxKwh || '',
      minKw: legacy.storageMinKw || '0',
      minKwh: legacy.storageMinKwh || '0',
      costPerKwh: legacy.storageCost || '500',
      reservePercent: legacy.batteryReservePercent || '20',
      chargeSource: legacy.bessChargeSource || 'pv_and_grid',
      canGridCharge: legacy.canGridCharge !== false,
      batteries: legacy.batteries || [{ id: 1, name: 'Battery 1', kw: '', kwh: '' }],
    },
    generator: {
      enabled: legacy.useGenerator || false,
      sizingMode: legacy.generatorSizingMode || 'prescribed',
      maxKw: legacy.generatorMaxKw || '',
      minKw: legacy.generatorMinKw || '0',
      fuelType: legacy.generatorFuelType || 'diesel',
      fuelCostPerGallon: legacy.generatorFuelCostPerGallon || '3',
      fuelCostPerMmbtu: legacy.generatorFuelCostPerMmbtu || '6',
      exportMode: legacy.generatorExportMode || 'none',
      runHoursLimit: legacy.generatorRunHoursLimit || '',
      uptimePercent: legacy.generatorUptimePercent || '96.5',
      thermalEfficiency: legacy.generatorThermalEfficiency || '',
      generators: legacy.generators || [],
    },
    tariff: {
      source: legacy.rateStructureType || 'blended',
      tariffId: legacy.customTariffData?.id || null,
      blendedEnergyRate: legacy.blendedEnergyRate || '0.12',
      blendedDemandRate: legacy.blendedDemandRate || '15.0',
      monthlyFixedCharge: legacy.monthlyFixedCharge || '25.0',
      touData: legacy.touPeriods ? { periods: legacy.touPeriods } : null,
      selectedTariffData: legacy.selectedTariffData || null,
      exportConfig: legacy.exportConfig || {
        enabled: false,
        mode: 'none',
        rate: '',
        tariffId: null,
        tariffData: null,
      },
      caDerConfig: legacy.caDerConfig || null,
      gasTariffId: legacy.gasTariffId || null,
      gasTariffData: legacy.gasTariffData || null,
    },
    ui: {
      inputMode: legacy.inputMode || 'ai_assisted',
      aiExtractionComplete: legacy.aiExtractionComplete || false,
      aiExtractedData: legacy.aiExtractedData || null,
      calculationMethod: legacy.calculationMethod || 'reopt',
      activeTab: legacy.tab || 0,
    },
    commercialAssumptions: legacy.commercialAssumptions || null,
    dealAnalysisConfig: legacy.dealAnalysisConfig || null,
  };
}

/**
 * Custom hook for components that want to use form context
 * but need backward compatibility with prop-based approach
 */
export function useScenarioFormContext() {
  try {
    // Try to get RHF context
    const { useFormContext } = require('react-hook-form');
    const context = useFormContext();

    if (context) {
      return {
        isRHF: true,
        ...context,
      };
    }
  } catch (e) {
    // No context available
  }

  return {
    isRHF: false,
    watch: () => undefined,
    setValue: () => {},
    getValues: () => ({}),
  };
}

/**
 * Create a sync callback that maps RHF field changes to legacy setters
 *
 * @param {Object} legacySetters - Object containing all legacy setter functions from useScenarioForm
 * @returns {Function} Sync callback for ScenarioFormProvider
 *
 * Usage:
 * ```jsx
 * const syncToLegacy = createLegacySyncCallback(scenarioForm);
 * <ScenarioFormProvider syncToLegacy={syncToLegacy} ... />
 * ```
 */
export function createLegacySyncCallback(legacySetters) {
  // Map RHF field paths to legacy setter names
  const fieldMapping = {
    // Site fields
    'site.scenarioName': 'setScenarioName',
    'site.siteName': 'setSiteName',
    'site.street': 'setStreet',
    'site.city': 'setCity',
    'site.state': 'setState',
    'site.zipCode': 'setZipCode',
    'site.siteDescription': 'setSiteDescription',
    'site.facilityType': 'setFacilityType',
    'site.utilityName': 'setUtilityName',
    'site.serviceClass': 'setServiceClass',
    'site.offGrid': 'setOffGrid',
    'site.latitude': 'setLatitude',
    'site.longitude': 'setLongitude',

    // Solar fields
    'solar.enabled': 'setUsePv',
    'solar.sizingMode': 'setSizingMode',
    'solar.maxKw': 'setPvMaxKw',
    'solar.costPerKw': 'setPvCost',
    'solar.exportMode': 'setPvExportMode',

    // Battery fields
    'battery.enabled': 'setUseStorage',
    'battery.sizingMode': 'setBatterySizingMode',
    'battery.maxKw': 'setStorageMaxKw',
    'battery.maxKwh': 'setStorageMaxKwh',
    'battery.reservePercent': 'setBatteryReservePercent',
    'battery.chargeSource': 'setBessChargeSource',
    'battery.batteries': 'setBatteries',

    // Generator fields
    'generator.enabled': 'setUseGenerator',
    'generator.sizingMode': 'setGeneratorSizingMode',
    'generator.maxKw': 'setGeneratorMaxKw',
    'generator.fuelType': 'setGeneratorFuelType',
    'generator.exportMode': 'setGeneratorExportMode',
    'generator.runHoursLimit': 'setGeneratorRunHoursLimit',
    'generator.uptimePercent': 'setGeneratorUptimePercent',
    'generator.thermalEfficiency': 'setGeneratorThermalEfficiency',
    'generator.generators': 'setGenerators',

    // Tariff fields
    'tariff.blendedEnergyRate': 'setBlendedEnergyRate',
    'tariff.blendedDemandRate': 'setBlendedDemandRate',
    'tariff.monthlyFixedCharge': 'setMonthlyFixedCharge',
    'tariff.selectedTariffData': 'setSelectedTariffData',
    'tariff.exportConfig': 'setExportConfig',
    'tariff.caDerConfig': 'setCaDerConfig',
    'tariff.gasTariffId': 'setGasTariffId',
    'tariff.gasTariffData': 'setGasTariffData',
  };

  return (fieldPath, values) => {
    const setterName = fieldMapping[fieldPath];
    if (setterName && legacySetters[setterName]) {
      // Get the value from the nested path
      const pathParts = fieldPath.split('.');
      let value = values;
      for (const part of pathParts) {
        value = value?.[part];
      }
      legacySetters[setterName](value);
    }
  };
}

export default ScenarioFormProvider;
